import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';

import { cn } from '@/lib/utils';

type ProgressProps = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
  /** When true, shows a flowing bar animation without a specific value. */
  indeterminate?: boolean;
  /** Optional extra classes for the indicator element. */
  indicatorClassName?: string;
};

const Progress = React.forwardRef<
  React.ComponentRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, indeterminate, indicatorClassName, ...props }, ref) => {
  const isDeterminate = !indeterminate && typeof value === 'number';

  return (
    <ProgressPrimitive.Root
      ref={ ref }
      className={ cn(
        'bg-fill-surface-strong border-border-neutral-medium relative h-4 w-full overflow-hidden rounded-full border',
        className,
      ) }
      // Only forward value in determinate mode
      { ...(isDeterminate ? { value } : {}) }
      { ...props }
    >
      <ProgressPrimitive.Indicator
        key={ isDeterminate ? 'determinate' : 'indeterminate' }
        className={ cn(
          'bg-fill-controls-primary-default h-full flex-1 transition-all',
          isDeterminate ? 'w-full' : 'w-1/2 animate-progress-indeterminate',
          indicatorClassName,
        ) }
        style={
          isDeterminate && typeof value === 'number'
            ? { transform: `translateX(-${100 - value}%)` }
            : undefined
        }
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
